// Sidebar navigation items + workspace defaults.
// Extracted from App.js so the App component stays focused on routing/state.

import {
  House, BookOpenText, ListChecks, FileText, ChartBar, CurrencyDollar, Export,
  ShieldCheck, ClockCounterClockwise, Crown, Calendar, Pill, Share, Pulse,
  PencilLine, UsersThree, CalendarPlus, Stethoscope,
} from "@phosphor-icons/react";

/**
 * Sidebar items grouped into three sections so overwhelmed users see a short
 * mental index instead of 15 flat rows. Order matches the actual veteran flow:
 *
 *  1. Build — claim structure: dashboard, conditions, evidence/tracker, forms, statements
 *  2. Track — ongoing medical signal: appointments, prescriptions, symptoms, calendar sync, IME
 *  3. Ship  — exports, sharing, finance/math, billing, backups, privacy
 *
 * `baseNavItems` is kept as a flat array (for the existing flat fallback /
 * tests / e2e) but `navGroups` is what the redesigned sidebar consumes.
 */

export const navGroups = [
  {
    id: "build",
    label: "Build",
    items: [
      { id: "dashboard",  label: "Command Center",   icon: House },
      { id: "conditions", label: "Condition Library", icon: BookOpenText },
      { id: "tracker",    label: "Claim Tracker",    icon: ListChecks },
      { id: "forms",      label: "Master Forms",     icon: FileText },
      { id: "statements", label: "Statements",       icon: PencilLine },
    ],
  },
  {
    id: "track",
    label: "Track",
    items: [
      { id: "ime",            label: "Visit Prep",      icon: Stethoscope },
      { id: "appointments",   label: "Appointments",    icon: Calendar },
      { id: "prescriptions",  label: "Prescriptions",   icon: Pill },
      { id: "symptoms",       label: "Symptom Tracker", icon: Pulse },
      { id: "calendar-sync",  label: "Calendar Sync",   icon: CalendarPlus },
    ],
  },
  {
    id: "ship",
    label: "Ship & Plan",
    items: [
      { id: "export",     label: "Export / Print",  icon: Export },
      { id: "shares",     label: "Share Links",     icon: Share },
      { id: "math",       label: "VA Math",         icon: ChartBar },
      { id: "financials", label: "Financials",      icon: CurrencyDollar },
      { id: "backups",    label: "Backups",         icon: ClockCounterClockwise },
      { id: "billing",    label: "Plan & Billing",  icon: Crown },
      { id: "privacy",    label: "Privacy & Tests", icon: ShieldCheck },
    ],
  },
];

// Flat list — preserved so any consumer expecting the old shape still works.
export const baseNavItems = navGroups.flatMap((g) => g.items);

export const ADMIN_NAV_ITEM = { id: "admin", label: "Admin", icon: UsersThree };

// Admin shows up as its own one-item "Admin" group at the bottom for admin users.
export const ADMIN_NAV_GROUP = {
  id: "admin",
  label: "Admin",
  items: [ADMIN_NAV_ITEM],
};

import { CLAIM_TYPES } from "@/lib/claimEnums";

export const DEFAULTS = {
  workspace: { title: "2026 VA Claim Workspace", claimType: CLAIM_TYPES.bdd },
  profile: { name: "Veteran", maritalStatus: "Single", minorDependents: 0, retirementStatus: "Not retired", paygrade: "E-7", yearsServed: 20, monthsServed: 0 },
  financials: { high3Average: "", yearsService: 20, monthsService: 0 },
};

// ---------------------------------------------------------------------------
// VIEW IDS — canonical, frozen, single source of truth.
//
// Callsites that want to navigate (e.g. `setActiveView(VIEWS.forms)`) or render
// (`activeView === VIEWS.forms`) should import from here instead of hardcoding
// strings. If a nav item id is ever renamed, the lookup blows up at *lint
// time* (undefined property reference) instead of silently rendering a blank
// page like the recent `"master"` → `"forms"` regression.
//
// Keys MUST mirror the `id` field of each entry in `navGroups` /
// `ADMIN_NAV_ITEM`. The module-level assertion just below enforces parity at
// import time so a renamed nav item without a matching VIEWS key crashes the
// app immediately during dev.
// ---------------------------------------------------------------------------
export const VIEWS = Object.freeze({
  dashboard: "dashboard",
  conditions: "conditions",
  tracker: "tracker",
  forms: "forms",
  statements: "statements",
  ime: "ime",
  appointments: "appointments",
  prescriptions: "prescriptions",
  symptoms: "symptoms",
  calendarSync: "calendar-sync",
  export: "export",
  shares: "shares",
  math: "math",
  financials: "financials",
  backups: "backups",
  billing: "billing",
  privacy: "privacy",
  preferences: "preferences",
  admin: "admin",
});

// Parity check — every nav item must have a VIEWS entry, and every VIEWS entry
// (except synthetic ones like `preferences`) must correspond to a nav item.
// Runs at module load so the dev server crashes loudly on drift.
const _navIds = new Set([...baseNavItems, ADMIN_NAV_ITEM].map((n) => n.id));
const _viewIds = new Set(Object.values(VIEWS));
// Synthetic views not in the sidebar (reachable only via user menu / direct nav).
const _SYNTHETIC = new Set(["preferences"]);
for (const id of _navIds) {
  if (!_viewIds.has(id)) {
    throw new Error(`navConfig: nav item "${id}" has no VIEWS entry. Add VIEWS.${id.replace(/-([a-z])/g, (_, c) => c.toUpperCase())} = "${id}".`);
  }
}
for (const id of _viewIds) {
  if (!_navIds.has(id) && !_SYNTHETIC.has(id)) {
    throw new Error(`navConfig: VIEWS contains "${id}" but no matching nav item exists.`);
  }
}

/** True if the given value is a known view id. Useful for runtime guards. */
export function isValidView(id) {
  return _viewIds.has(id);
}
